import {
  PWA_INSTALLED_DISPLAY_MODES,
  PWA_INSTALL_SNOOZE_MS,
  PWA_INSTALL_STORAGE_PREFIX
} from '../constants/pwaInstallConfig';

let deferredInstallPrompt = null;
let captureStarted = false;
let installedEventObserved = false;
const stateListeners = new Set();

function getNavigator() {
  return typeof navigator === 'undefined' ? null : navigator;
}

function getDocument() {
  return typeof document === 'undefined' ? null : document;
}

function getMatchMedia() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia.bind(window)
    : null;
}

function emitStateChange() {
  const state = getPwaInstallRuntimeState();
  stateListeners.forEach((listener) => listener(state));
}

function getPreferenceKey(userId) {
  return `${PWA_INSTALL_STORAGE_PREFIX}:${userId || 'anonymous'}`;
}

function readPreference(userId) {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    return JSON.parse(localStorage.getItem(getPreferenceKey(userId))) || {};
  } catch (error) {
    return {};
  }
}

function writePreference(userId, value) {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(getPreferenceKey(userId), JSON.stringify(value));
  } catch (error) {
    // 설치 안내 환경설정 저장 실패가 앱 사용을 막아서는 안 된다.
  }
}

export function isIosDevice(navigatorObject = getNavigator()) {
  if (!navigatorObject) {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigatorObject.userAgent || '')
    || (navigatorObject.platform === 'MacIntel' && navigatorObject.maxTouchPoints > 1);
}

export function isSafariBrowser(navigatorObject = getNavigator()) {
  const userAgent = navigatorObject?.userAgent || '';
  return /Safari/i.test(userAgent) && !/(CriOS|FxiOS|EdgiOS|OPiOS|Android)/i.test(userAgent);
}

export function isAndroidDevice(navigatorObject = getNavigator()) {
  return /Android/i.test(navigatorObject?.userAgent || '');
}

export function isRunningAsInstalledPwa({
  navigatorObject = getNavigator(),
  documentObject = getDocument(),
  matchMediaFunction = getMatchMedia()
} = {}) {
  const displayModeMatches = PWA_INSTALLED_DISPLAY_MODES.some((mode) => {
    try {
      return Boolean(matchMediaFunction?.(`(display-mode: ${mode})`).matches);
    } catch (error) {
      return false;
    }
  });

  return displayModeMatches
    || navigatorObject?.standalone === true
    || documentObject?.referrer?.startsWith('android-app://') === true;
}

export function getPwaInstallRuntimeState() {
  const navigatorObject = getNavigator();
  const isStandalone = isRunningAsInstalledPwa();
  const isInstalled = installedEventObserved || isStandalone;

  return {
    isInstalled,
    isStandalone,
    isIos: isIosDevice(navigatorObject),
    isSafari: isSafariBrowser(navigatorObject),
    isAndroid: isAndroidDevice(navigatorObject),
    canPrompt: !isInstalled && Boolean(deferredInstallPrompt)
  };
}

export function startPwaInstallPromptCapture() {
  if (captureStarted || typeof window === 'undefined') {
    return;
  }

  captureStarted = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    emitStateChange();
  });

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    installedEventObserved = true;
    emitStateChange();
  });

  PWA_INSTALLED_DISPLAY_MODES.forEach((mode) => {
    const mediaQuery = getMatchMedia()?.(`(display-mode: ${mode})`);
    mediaQuery?.addEventListener?.('change', emitStateChange);
  });
}

export function subscribePwaInstallState(listener) {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

export async function promptPwaInstall() {
  if (!deferredInstallPrompt) {
    return { outcome: 'unavailable' };
  }

  const promptEvent = deferredInstallPrompt;
  deferredInstallPrompt = null;
  emitStateChange();

  try {
    const promptResult = await promptEvent.prompt();
    const choice = promptEvent.userChoice
      ? await promptEvent.userChoice
      : promptResult;
    return { outcome: choice?.outcome || 'dismissed' };
  } catch (error) {
    return { outcome: 'error', error };
  }
}

export function shouldShowPwaInstallPromotion(userId, now = Date.now()) {
  const preference = readPreference(userId);
  return preference.confirmedInstalled !== true
    && Number(preference.dismissedUntil || 0) <= now;
}

export function snoozePwaInstallPromotion(userId, duration = PWA_INSTALL_SNOOZE_MS, now = Date.now()) {
  writePreference(userId, {
    ...readPreference(userId),
    dismissedUntil: now + duration
  });
}

export function markPwaInstallPromotionComplete(userId) {
  writePreference(userId, {
    ...readPreference(userId),
    confirmedInstalled: true,
    dismissedUntil: 0
  });
}
